import React, { useCallback, useRef } from 'react';
import './PeriodStatusButtonGroupAtom.scss';

export interface PeriodStatusButtonGroupOption {
  /** Stable identifier emitted through onChange. */
  value: string;
  /** Localized, human-readable label — shown on hover via tooltip. */
  label: string;
  /** Full PrimeIcons class string (e.g. "pi pi-check-circle"). */
  icon: string;
  /** Fill color for the selected state. CSS color or var() reference. */
  color: string;
  /** Optional; omit the option entirely rather than rely on disabled when possible. */
  disabled?: boolean;
}

export interface PeriodStatusButtonGroupAtomProps {
  /** Status options rendered as icon-only buttons in insertion order. */
  statuses: PeriodStatusButtonGroupOption[];
  /** Currently-selected status value, or null when nothing is selected. */
  value: string | null;
  /** Fires with the status value when the user clicks or keyboard-selects a button. */
  onChange: (statusValue: string) => void;
  /**
   * Prefix for each button's aria-label. Final label is `${ariaLabelPrefix} ${status.label}`.
   * Consumers supply a localized prefix (e.g. "Set status to").
   */
  ariaLabelPrefix?: string;
  /** Disables the entire group. Individual options can also set `disabled`. */
  disabled?: boolean;
  className?: string;
}

const PeriodStatusButtonGroupAtom: React.FC<PeriodStatusButtonGroupAtomProps> = ({
  statuses,
  value,
  onChange,
  ariaLabelPrefix,
  disabled = false,
  className = '',
}) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusAt = useCallback((index: number) => {
    const button = buttonRefs.current[index];
    if (button) {
      button.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number, optionValue: string, optionDisabled: boolean) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        const next = (index + 1) % statuses.length;
        focusAt(next);
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = (index - 1 + statuses.length) % statuses.length;
        focusAt(prev);
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        focusAt(0);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        focusAt(statuses.length - 1);
        return;
      }
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        if (!disabled && !optionDisabled) {
          onChange(optionValue);
        }
      }
    },
    [statuses.length, focusAt, onChange, disabled]
  );

  const classes = ['period-status-button-group-atom', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="radiogroup" aria-disabled={disabled || undefined}>
      {statuses.map((status, index) => {
        const isSelected = status.value === value;
        const optionDisabled = disabled || Boolean(status.disabled);
        const ariaLabel = ariaLabelPrefix ? `${ariaLabelPrefix} ${status.label}` : status.label;
        const buttonClasses = [
          'period-status-button-group-atom__button',
          isSelected && 'period-status-button-group-atom__button--selected',
        ]
          .filter(Boolean)
          .join(' ');
        const buttonStyle: React.CSSProperties = isSelected
          ? { backgroundColor: status.color, borderColor: status.color }
          : {};
        return (
          <button
            key={status.value}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={ariaLabel}
            title={status.label}
            disabled={optionDisabled}
            className={buttonClasses}
            style={buttonStyle}
            onClick={() => {
              if (!optionDisabled) {
                onChange(status.value);
              }
            }}
            onKeyDown={(event) => handleKeyDown(event, index, status.value, Boolean(status.disabled))}
          >
            <i
              className={status.icon}
              style={isSelected ? undefined : { color: status.color }}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
};

PeriodStatusButtonGroupAtom.displayName = 'PeriodStatusButtonGroupAtom';

export default PeriodStatusButtonGroupAtom;
